import { __stringify, _keys, BadImplementationException, currentTimeMillis, exists, Format_YYYYMMDD_HHmmss, formatTimestamp, MimeType_json, Module, Second, sleep } from '@nu-art/ts-common';
import { dispatch_onRuntimeSmartDocUpdated, ModuleFE_AdvisorSession, ModuleFE_EMInterface, ModuleFE_RuntimeSmartDoc, } from '@app/advisor-frontend';
import { ModuleFE_LocalStorage, ModuleFE_Thunderstorm } from '@nu-art/thunderstorm-frontend/index';
import { StorageKey_devMode, StorageKey_EnableKickstart, StorageKey_hideNegative, StorageKey_NumberOfKickstartQuestions, StorageKey_ShowOrderSetProtocols } from './storage-keys.js';
import { advisorConfig_DevMode, advisorConfig_EnableKickstart, advisorConfig_HideNegative, advisorConfig_NumberOfKickstartQuestions, advisorConfig_ShowOrderSetProtocols, } from './types.js';
import { dispatch_AdvisorSettingsUpdated } from './dispatcher.js';
import { DefaultKickstartVariables } from '@app/advisor-shared';
import { ModuleFE_AdvisorAnalytics } from '../../_analytics/consts.js';
class ModuleFE_AdvisorSettings_Class extends Module {
    advisorConfiguration;
    static setDefaultValues = () => {
        // set default values to storage keys if doesn't exists
        if (!exists(StorageKey_hideNegative.get()))
            StorageKey_hideNegative.set(true);
        if (!exists(StorageKey_devMode.get()))
            StorageKey_devMode.set(false);
        if (!exists(StorageKey_EnableKickstart.get()))
            StorageKey_EnableKickstart.set(false);
        if (!exists(StorageKey_NumberOfKickstartQuestions.get()))
            StorageKey_NumberOfKickstartQuestions.set(DefaultKickstartVariables);
        if (!exists(StorageKey_ShowOrderSetProtocols.get()))
            StorageKey_ShowOrderSetProtocols.set(false);
    };
    constructor() {
        super();
        // make sure default values is set in storage
        ModuleFE_AdvisorSettings_Class.setDefaultValues();
        // due to triggering value change of this value in a different module I add a listener to make sure we listen on changes here
        this.resetModuleCache();
    }
    resetModuleCache() {
        this.advisorConfiguration = {
            hideNegative: StorageKey_hideNegative.get(true), // default value is either true or whatever was previously defined on the machine
            devMode: StorageKey_devMode.get(false),
            enableKickstart: StorageKey_EnableKickstart.get(false), // kickstart is false by default
            numberOfKickstartQuestions: StorageKey_NumberOfKickstartQuestions.get(DefaultKickstartVariables),
            [advisorConfig_ShowOrderSetProtocols]: StorageKey_ShowOrderSetProtocols.get(false),
        };
    }
    iterateAndDeleteEncounterKeys = (encounterId) => {
        _keys(localStorage).forEach(storageKey => {
            // skip irrelevant keys
            if (!String(storageKey).includes(encounterId))
                return;
            localStorage.removeItem(storageKey.toString());
            ModuleFE_LocalStorage.clearCache(storageKey.toString());
        });
    };
    floodMemory = async () => {
        // show the memory monitor
        const memoryMonitorElement = document.getElementsByClassName('ts-memory-monitor')[0];
        memoryMonitorElement.style.display = 'block';
        //@ts-ignore
        // eslint-disable-next-line no-constant-condition
        while (true) {
            ModuleFE_RuntimeSmartDoc.cache._array = [...ModuleFE_RuntimeSmartDoc.cache._array, ...Array(1000).fill(ModuleFE_RuntimeSmartDoc.cache._array[0])];
            // @ts-ignore
            dispatch_onRuntimeSmartDocUpdated.dispatchUI('upsert-all', ModuleFE_RuntimeSmartDoc.cache._array);
            await sleep(3 * Second);
        }
    };
    toggleLogger = () => {
        const logger = document.getElementById('log');
        if (!logger)
            return;
        logger.classList.toggle('hidden');
        const loggerToggleButton = document.getElementById('logger-toggle');
        if (!loggerToggleButton)
            throw new BadImplementationException('Added a logger but didn\'t add logger toggle button.');
        loggerToggleButton.classList.toggle('hidden');
    };
    getFindingsDump = async () => {
        const { findings } = await ModuleFE_EMInterface.vv1.debug.getFindingsDump({}).executeSync();
        ModuleFE_Thunderstorm.downloadFile({
            fileName: `findings-dump-${formatTimestamp(Format_YYYYMMDD_HHmmss, currentTimeMillis())}.json`,
            content: __stringify(findings, true),
            mimeType: MimeType_json
        });
    };
    getEncounterDataExport = async () => {
        const { excel } = await ModuleFE_EMInterface.vv1.debug.getEncounterDataExport({}).executeSync();
        const binary = atob(excel);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++)
            bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        ModuleFE_Thunderstorm.downloadFile({
            url,
            fileName: `encounter-export-${formatTimestamp(Format_YYYYMMDD_HHmmss, currentTimeMillis())}.xlsx`
        });
    };
    clearEncounterCache = async (encounterId) => {
        if (encounterId) {
            await ModuleFE_AdvisorSession.cleanEncounterFromIDB(encounterId, true); // true here means to also clear this advisor instance's collection modules. False(default) is used to clean old encounters.
            this.iterateAndDeleteEncounterKeys(encounterId);
            // We do not delete sessionId, encounterId or patientId.
        }
        else {
            this.logError(`DEBUG - cannot cleanEncounterFromIDB because 'ModuleFE_AdvisorSession.currentEncounter' is undefined!`);
            return;
        }
    };
    reportSettingChanged = (settingLabel, fromValue, toValue) => {
        ModuleFE_AdvisorAnalytics.trigger.settings.settings_changed({
            settingLabel,
            fromValue: String(fromValue),
            toValue: String(toValue),
        });
    };
    toggleDevMode = () => {
        const fromValue = this.advisorConfiguration.devMode;
        this.advisorConfiguration.devMode = !fromValue;
        StorageKey_devMode.set(this.advisorConfiguration.devMode);
        this.reportSettingChanged(advisorConfig_DevMode, fromValue, this.advisorConfiguration.devMode);
        dispatch_AdvisorSettingsUpdated.dispatchAll(advisorConfig_DevMode);
    };
    toggleHideNegative = () => {
        const fromValue = this.advisorConfiguration.hideNegative;
        this.advisorConfiguration.hideNegative = !fromValue;
        StorageKey_hideNegative.set(this.advisorConfiguration.hideNegative);
        this.reportSettingChanged(advisorConfig_HideNegative, fromValue, this.advisorConfiguration.hideNegative);
        dispatch_AdvisorSettingsUpdated.dispatchAll(advisorConfig_HideNegative);
    };
    toggleKickstart = () => {
        const fromValue = this.advisorConfiguration.enableKickstart;
        this.advisorConfiguration.enableKickstart = !fromValue;
        StorageKey_EnableKickstart.set(this.advisorConfiguration.enableKickstart);
        this.reportSettingChanged(advisorConfig_EnableKickstart, fromValue, this.advisorConfiguration.enableKickstart);
        dispatch_AdvisorSettingsUpdated.dispatchAll(advisorConfig_EnableKickstart);
    };
    setNumberOfKickstartQuestions = (number) => {
        const fromValue = this.advisorConfiguration.numberOfKickstartQuestions;
        this.advisorConfiguration.numberOfKickstartQuestions = number;
        StorageKey_NumberOfKickstartQuestions.set(this.advisorConfiguration.numberOfKickstartQuestions);
        this.reportSettingChanged(advisorConfig_NumberOfKickstartQuestions, fromValue, number);
        dispatch_AdvisorSettingsUpdated.dispatchAll(advisorConfig_NumberOfKickstartQuestions);
    };
    toggleShowOrderSetProtocols = () => {
        const fromValue = this.advisorConfiguration[advisorConfig_ShowOrderSetProtocols];
        this.advisorConfiguration[advisorConfig_ShowOrderSetProtocols] = !fromValue;
        StorageKey_ShowOrderSetProtocols.set(this.advisorConfiguration[advisorConfig_ShowOrderSetProtocols]);
        this.reportSettingChanged(advisorConfig_ShowOrderSetProtocols, fromValue, this.advisorConfiguration[advisorConfig_ShowOrderSetProtocols]);
        dispatch_AdvisorSettingsUpdated.dispatchAll(advisorConfig_ShowOrderSetProtocols);
    };
    getAdvisorConfig = (key) => {
        return this.advisorConfiguration[key];
    };
}
export const ModuleFE_AdvisorSettings = new ModuleFE_AdvisorSettings_Class();
//# sourceMappingURL=ModuleFE_AdvisorSettings.js.map